import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft, IconMail, IconSparkles, IconCheck, IconLoader2, IconCopy } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const AUDIENCES = [
  { id: "familias", label_es: "Familias", label_en: "Families" },
  { id: "docentes", label_es: "Docentes", label_en: "Teachers" },
  { id: "escuelas", label_es: "Escuelas", label_en: "Schools" },
  { id: "psicologas", label_es: "Psicólogas", label_en: "Psychologists" },
];

export default function Boletin() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [signals, setSignals] = useState([]);
  const [checked, setChecked] = useState({});
  const [audience, setAudience] = useState("familias");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bulletin/signals");
      setSignals(data.signals);
      const c = {};
      data.signals.forEach((s, i) => { c[i] = true; });
      setChecked(c);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  })(); }, []);

  const generate = async () => {
    setError("");
    setGenLoading(true);
    setDraft(null);
    try {
      const selected = signals.filter((_, i) => checked[i]);
      const { data } = await api.post("/ai/bulletin/draft", {
        audience,
        signals: selected,
        lang,
      });
      setDraft(data.draft);
    } catch (e) { setError(formatApiError(e)); }
    finally { setGenLoading(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  const parsedDraft = React.useMemo(() => {
    if (!draft) return null;
    try {
      const m = draft.match(/\{[\s\S]*\}/);
      if (m) return JSON.parse(m[0]);
    } catch { /* fallthrough */ }
    return { subject: "", body: draft };
  }, [draft]);

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="boletin-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Boletín · Generador semanal" : "Bulletin · Weekly generator"}</span>
      </div>

      <div className="wrap-app max-w-[960px] py-10 grid lg:grid-cols-[380px_1fr] gap-6">
        <div>
          <p className="eyebrow mb-3">{lang === "es" ? "Señales de la red · últimos 7 días" : "Network signals · last 7 days"}</p>
          {loading ? (
            <div className="card"><IconLoader2 className="animate-spin" /></div>
          ) : (
            <div className="card space-y-2" data-testid="signals-list">
              {signals.map((s, i) => (
                <label key={i} className="flex items-start gap-2 text-[13px] leading-relaxed cursor-pointer" data-testid={`signal-${i}`}>
                  <input
                    type="checkbox"
                    checked={!!checked[i]}
                    onChange={(e) => setChecked({ ...checked, [i]: e.target.checked })}
                    className="mt-1"
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          )}

          <p className="lbl mt-6 mb-2">{lang === "es" ? "Público objetivo" : "Audience"}</p>
          <div className="grid grid-cols-2 gap-2">
            {AUDIENCES.map((a) => (
              <button
                key={a.id}
                className={`px-3 py-2 text-[12px] rounded-lg border transition-all ${
                  audience === a.id ? "border-teal bg-teal-soft text-teal-deep" : "border-line bg-white"
                }`}
                onClick={() => setAudience(a.id)}
                data-testid={`audience-${a.id}`}
              >
                {lang === "es" ? a.label_es : a.label_en}
              </button>
            ))}
          </div>

          <button
            className="btn mt-4 w-full"
            onClick={generate}
            disabled={genLoading}
            data-testid="boletin-generate"
          >
            {genLoading
              ? <><IconLoader2 size={13} className="animate-spin" /> {lang === "es" ? "Generando..." : "Generating..."}</>
              : <><IconSparkles size={13} /> {lang === "es" ? "Generar con Sistema Inteligente" : "Generate with Sistema Inteligente"}</>}
          </button>
          {error && <p className="text-errtext text-[13px] mt-3">{error}</p>}
        </div>

        <div>
          <p className="eyebrow mb-3 flex items-center gap-2"><IconMail size={14} /> {lang === "es" ? "Vista previa del boletín" : "Bulletin preview"}</p>
          {!draft && !genLoading ? (
            <div className="card text-tert text-[13px]" data-testid="boletin-empty">
              {lang === "es"
                ? "Selecciona las señales relevantes y presiona 'Generar'. Podrás editar el resultado y copiarlo antes de enviar."
                : "Select the relevant signals and press 'Generate'. You can edit and copy the result before sending."}
            </div>
          ) : genLoading ? (
            <div className="card flex items-center gap-2 text-tert"><IconLoader2 className="animate-spin" size={14} /> {lang === "es" ? "Redactando con Sistema Inteligente..." : "Drafting..."}</div>
          ) : (
            <div className="card space-y-4" data-testid="boletin-preview">
              {parsedDraft?.subject && (
                <div>
                  <p className="lbl m-0">{lang === "es" ? "Asunto" : "Subject"}</p>
                  <p className="font-display text-[20px] font-medium mt-1">{parsedDraft.subject}</p>
                </div>
              )}
              <div>
                <p className="lbl m-0">{lang === "es" ? "Cuerpo" : "Body"}</p>
                <textarea
                  className="textarea mt-1 min-h-[300px]"
                  value={parsedDraft?.body || draft}
                  onChange={(e) => setDraft(JSON.stringify({ subject: parsedDraft?.subject || "", body: e.target.value }))}
                  data-testid="boletin-body"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={copy} className="btn-ghost btn text-[12px]" data-testid="boletin-copy">
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  {copied ? (lang === "es" ? "Copiado" : "Copied") : (lang === "es" ? "Copiar" : "Copy")}
                </button>
                <span className="text-tert text-[11.5px] self-center">
                  {lang === "es"
                    ? "Sugerencia: pega en Resend, Mailchimp o ConvertKit y envía a la lista de la audiencia elegida."
                    : "Tip: paste into Resend, Mailchimp or ConvertKit and send to the audience list."}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
